<template>
  <div class="time-picker-container">
    <div class="time-picker">
      <label>{{ $t('base.period') }} {{ $t('base.from') }}</label>
      <div class="custom-time-picker">
        <input
          type="text"
          v-model="startHour"
          @input="validateStartHour"
          @blur="formatStartHour"
          :maxlength="isUSFormat ? 2 : 2"
        />
        <span>:</span>
        <input
          type="text"
          v-model="startMinute"
          @input="validateStartMinute"
          @blur="formatMinute('startMinute', false)"
          maxlength="2"
        />
        <select v-if="isUSFormat" v-model="startPeriod" @change="onStartPeriodChange" class="period-select">
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
    <div class="time-picker">
      <label>{{ $t('base.period') }} {{ $t('base.to') }}</label>
      <div class="custom-time-picker">
        <input
          type="text"
          v-model="endHour"
          @input="validateEndHour"
          @blur="formatEndHour"
          :maxlength="isUSFormat ? 2 : 2"
        />
        <span>:</span>
        <input
          type="text"
          v-model="endMinute"
          @input="validateEndMinute"
          @blur="formatMinute('endMinute', false)"
          maxlength="2"
        />
        <select v-if="isUSFormat" v-model="endPeriod" @change="emitTimeRange" class="period-select">
          <option value="AM" :disabled="startPeriod === 'PM'">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
    <p v-if="timeInvalid" class="error-text">
      {{ $t('errors.timeRangeInvalid') }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'TimeField',
  props: {
    from: {
      type: String,
      default: '06:00:00',
    },
    to: {
      type: String,
      default: '11:00:00',
    },
  },
  data () {
    const isUS = this.$store.getters['account/lang'] === 'us'
    const [fromHour, fromMinute] = this.from.split(':')
    const [toHour, toMinute] = this.to.split(':')

    if (isUS) {
      const start = this.convertTo12Hour(parseInt(fromHour))
      const end = this.convertTo12Hour(parseInt(toHour))

      return {
        startHour: start.hour.padStart(2, '0'),
        startMinute: fromMinute.padStart(2, '0'),
        startPeriod: start.period,
        endHour: end.hour.padStart(2, '0'),
        endMinute: toMinute.padStart(2, '0'),
        endPeriod: end.period,
        timeInvalid: false,
      }
    } else {
      return {
        startHour: fromHour.padStart(2, '0'),
        startMinute: fromMinute.padStart(2, '0'),
        startPeriod: 'AM',
        endHour: toHour.padStart(2, '0'),
        endMinute: toMinute.padStart(2, '0'),
        endPeriod: 'PM',
        timeInvalid: false,
      }
    }
  },
  computed: {
    currentLang () {
      return this.$store.getters['account/lang']
    },
    isUSFormat () {
      return this.currentLang === 'us'
    }
  },
  methods: {
    convertTo12Hour (hour24) {
      if (hour24 === 0) return { hour: '12', period: 'AM' }
      if (hour24 < 12) return { hour: String(hour24), period: 'AM' }
      if (hour24 === 12) return { hour: '12', period: 'PM' }
      return { hour: String(hour24 - 12), period: 'PM' }
    },
    convertTo24Hour (hour12, period) {
      let hour = parseInt(hour12)
      if (period === 'AM') {
        if (hour === 12) hour = 0
      } else {
        if (hour !== 12) hour += 12
      }
      return hour
    },
    get24HourTime (hour, minute, period) {
      if (this.isUSFormat) {
        const hour24 = this.convertTo24Hour(hour, period)
        return { hour: hour24, minute: parseInt(minute) }
      }
      return { hour: parseInt(hour), minute: parseInt(minute) }
    },
    onStartPeriodChange () {
      // Якщо start period змінився на PM, а end period = AM, то змінюємо end на PM
      if (this.startPeriod === 'PM' && this.endPeriod === 'AM') {
        this.endPeriod = 'PM'
      }
      this.emitTimeRange()
    },
    emitTimeRange () {
      // Always ensure "to" time is not earlier than "from" time
      this.adjustTimeRangeIfNeeded()

      // Get the formatted values for emission
      const startH = this.startHour === '' ? '00' : this.startHour.padStart(2, '0')
      const startM = this.startMinute === '' ? '00' : this.startMinute.padStart(2, '0')
      const endH = this.endHour === '' ? '00' : this.endHour.padStart(2, '0')
      const endM = this.endMinute === '' ? '00' : this.endMinute.padStart(2, '0')

      if (!this.timeInvalid) {
        let finalStartH, finalEndH

        if (this.isUSFormat) {
          finalStartH = String(this.convertTo24Hour(startH, this.startPeriod)).padStart(2, '0')
          finalEndH = String(this.convertTo24Hour(endH, this.endPeriod)).padStart(2, '0')
        } else {
          finalStartH = startH
          finalEndH = endH
        }

        this.$emit('updateTime',
          `${finalStartH}:${startM}:00`,
          `${finalEndH}:${endM}:00`
        )
      }
    },
    adjustTimeRangeIfNeeded () {
      // If any field is empty, skip the validation
      if (this.startHour === '' || this.startMinute === '' ||
          this.endHour === '' || this.endMinute === '') {
        this.timeInvalid = false
        return
      }

      // When the end hour is only a single digit (user is mid-typing), don't adjust yet
      if (this.endHour.length === 1 && this.endHour >= '0' && this.endHour <= '9') {
        this.timeInvalid = false
        return
      }

      const startTime = this.get24HourTime(this.startHour, this.startMinute, this.startPeriod)
      const endTime = this.get24HourTime(this.endHour, this.endMinute, this.endPeriod)

      // Check if from time is later than to time
      if (startTime.hour > endTime.hour ||
         (startTime.hour === endTime.hour && startTime.minute >= endTime.minute)) {
        // Automatically update end time to match start time
        this.endHour = this.startHour
        this.endMinute = this.startMinute
        if (this.isUSFormat) {
          this.endPeriod = this.startPeriod
        }
        this.timeInvalid = false
      } else {
        this.timeInvalid = false
      }
    },
    validateStartHour () {
      // Allow empty value during input
      if (this.startHour === '') return

      let value = Number(this.startHour)
      if (isNaN(value)) {
        this.startHour = ''
      } else if (value < 1) {
        this.startHour = this.isUSFormat ? '01' : '00'
      } else if (this.isUSFormat && value > 12) {
        this.startHour = '12'
      } else if (!this.isUSFormat && value > 23) {
        this.startHour = '23'
      }

      // After changing start hour, adjust end time if needed
      this.adjustTimeRangeIfNeeded()
    },
    validateStartMinute () {
      // Allow empty value during input
      if (this.startMinute === '') return

      this.formatMinute('startMinute', true)
      this.adjustTimeRangeIfNeeded()
    },
    validateEndHour () {
      // Allow empty value during input
      if (this.endHour === '') return

      // Don't validate single digit hour values while typing
      if (this.endHour.length === 1 && this.endHour >= '0' && this.endHour <= '9') {
        return
      }

      let value = Number(this.endHour)
      if (isNaN(value)) {
        this.endHour = ''
      } else if (value < 1) {
        this.endHour = this.isUSFormat ? '01' : '00'
      } else if (this.isUSFormat && value > 12) {
        this.endHour = '12'
      } else if (!this.isUSFormat && value > 23) {
        this.endHour = '23'
      }

      // After changing end hour, check if time range is valid
      this.adjustTimeRangeIfNeeded()
    },
    validateEndMinute () {
      // Allow empty value during input
      if (this.endMinute === '') return

      this.formatMinute('endMinute', true)
      this.adjustTimeRangeIfNeeded()
    },
    formatStartHour () {
      // If empty, set to default
      if (this.startHour === '') {
        this.startHour = this.isUSFormat ? '01' : '00'
      } else {
        // First validate to ensure proper range
        this.validateStartHour()
        // Then format to ensure proper padding
        this.startHour = this.startHour.padStart(2, '0')
      }
      this.emitTimeRange()
    },
    formatEndHour () {
      // If empty, set to default
      if (this.endHour === '') {
        this.endHour = this.isUSFormat ? '01' : '00'
      } else {
        // First validate to ensure proper range
        this.validateEndHour()
        // Then format to ensure proper padding
        this.endHour = this.endHour.padStart(2, '0')
      }
      this.emitTimeRange()
    },
    formatMinute (minuteField, allowEmpty = false) {
      if (this[minuteField] === '' && allowEmpty) return

      // If empty and not during input, set default
      if (this[minuteField] === '' && !allowEmpty) {
        this[minuteField] = '00'
        return
      }

      let minute = Number(this[minuteField])
      if (isNaN(minute)) {
        this[minuteField] = allowEmpty ? '' : '00'
      } else if (minute < 0) {
        this[minuteField] = '00'
      } else if (minute > 59) {
        this[minuteField] = '59'
      } else {
        // Only add padding when field loses focus or during emit
        if (!allowEmpty) {
          this[minuteField] = String(minute).padStart(2, '0')
        }
      }
      this.emitTimeRange()
    },
  },
  watch: {
    currentLang: {
      handler (newLang, oldLang) {
        if (newLang !== oldLang) {
          // Convert current values when language changes
          if (newLang === 'us') {
            // Convert to 12-hour format
            const startHour24 = parseInt(this.startHour)
            const endHour24 = parseInt(this.endHour)

            const startConverted = this.convertTo12Hour(startHour24)
            const endConverted = this.convertTo12Hour(endHour24)

            this.startHour = startConverted.hour
            this.startPeriod = startConverted.period
            this.endHour = endConverted.hour
            this.endPeriod = endConverted.period
          } else {
            // Convert to 24-hour format
            this.startHour = String(this.convertTo24Hour(this.startHour, this.startPeriod)).padStart(2, '0')
            this.endHour = String(this.convertTo24Hour(this.endHour, this.endPeriod)).padStart(2, '0')
          }
          this.emitTimeRange()
        }
      },
      immediate: false
    }
  },
  mounted () {
    this.adjustTimeRangeIfNeeded()
    this.emitTimeRange()
  },
}
</script>

<style scoped>
.time-picker-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.time-picker {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: space-between;
}
.time-picker label {
    font-size: 1.6rem;
    font-weight: 600;
}
.custom-time-picker {
  display: flex;
  align-items: center;
  gap: 5px;
}
input[type="text"] {
  background: #F4F4F4;
  color: #000;
  padding: 0.5rem;
  border-bottom: 1px solid #D8D8D8;
  font-size: 1.6rem;
  width: 40px;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
}
.period-select {
  background: #F4F4F4;
  color: #000;
  padding: 0.5rem;
  border-bottom: 1px solid #D8D8D8;
  font-size: 1.6rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-radius: 0;
}
.period-select option:disabled {
  background-color: #e0e0e0;
  color: #999;
}
.error-text {
  color: red;
  font-size: 12px;
}
</style>